import { Group, Progress, Stack, Text } from "@mantine/core";

import { formatMacroValue } from "./macro-aggregations";

type MacroTargetCellProps = {
  actualValue: number;
  targetValue: number | null | undefined;
  targetLabel?: string;
};

const clampProgress = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  if (value < 0) {
    return 0;
  }

  return Math.min(value, 100);
};

export const MacroTargetCell = ({ actualValue, targetValue, targetLabel = "target" }: MacroTargetCellProps) => {
  const hasTarget = typeof targetValue === "number" && Number.isFinite(targetValue);
  const resolvedTarget = hasTarget ? (targetValue as number) : null;
  const delta = resolvedTarget === null ? null : actualValue - resolvedTarget;
  const isOnTarget = delta !== null ? Math.abs(delta) < 0.05 : false;
  const displayTarget = hasTarget ? `${formatMacroValue(resolvedTarget ?? 0)} ${targetLabel}` : "";
  const progressValue =
    resolvedTarget !== null && resolvedTarget > 0 ? clampProgress((actualValue / resolvedTarget) * 100) : 0;

  const progressColor = delta === null ? "gray" : delta > 0 ? "orange" : delta < 0 ? "teal" : "gray";
  const deltaColor = delta === null ? "#6B7280" : delta > 0 ? "#B45309" : delta < 0 ? "#047857" : "#111827";
  const deltaLabel =
    delta === null
      ? ``
      : isOnTarget
        ? "On target"
        : `${formatMacroValue(Math.abs(delta))} ${delta > 0 ? "over" : "under"}`;

  return (
    <Stack gap={4} style={{ minWidth: 140 }}>
      <Group justify="space-between" gap={4} align="flex-start">
        <Text fw={600}>{formatMacroValue(actualValue)}</Text>
        <Text size="xs" style={{ color: "#6B7280" }}>
          {displayTarget}
        </Text>
      </Group>
      {hasTarget ? (
        <>
          <Progress value={progressValue} color={progressColor} radius="xl" size="sm" />
          <Text size="xs" fw={500} style={{ color: deltaColor }}>
            {deltaLabel}
          </Text>
        </>
      ) : (
        <Text size="xs" style={{ color: "#9CA3AF" }}>
          {deltaLabel}
        </Text>
      )}
    </Stack>
  );
};
